import { motion } from "framer-motion";
import { Phone, ShieldCheck, Gift, Percent, BadgeCheck, ArrowRight } from "lucide-react";
import { SITE } from "../data/site";
import { PHONE_SERIES } from "../data/phonesForSale";
import { useLang } from "../i18n";
import { MaskLines, Reveal, Stagger, StaggerItem } from "../components/Reveal";
import { Marquee } from "../components/Marquee";
import { PhoneFloat, PhoneBgDrift } from "../components/PhoneFloat";
import { BrandMark } from "../components/BrandMark";
import { TechRail } from "../components/TechRail";
import { SolarSystem } from "../components/SolarSystem";
import { Tilt3D } from "../components/Motion";
import { DeviceDrift } from "../components/DeviceDrift";
import { Jelly, PhraseFloat } from "../components/Motion";
import { BrandMarquee } from "../components/BrandMarquee";

const EASE = [0.22, 1, 0.36, 1];
const PERK_ICONS = [ShieldCheck, Gift, Percent, BadgeCheck];
const PERK_COLORS = ["text-volt", "text-accent-bright", "text-cyan", "text-magenta"];

const Hero = () => {
  const { t } = useLang();
  return (
    <section data-testid="phones-hero" className="grain relative overflow-hidden pt-32 sm:pt-40 pb-16">
      <div className="pointer-events-none absolute -top-24 -right-24 h-[26rem] w-[26rem] rounded-full bg-volt/15 blur-3xl" aria-hidden="true" />
      <div className="pointer-events-none absolute top-40 -left-24 h-[24rem] w-[24rem] rounded-full bg-magenta/15 blur-3xl" aria-hidden="true" />

      {/* floating official brand logos */}
      <BrandMark brand="apple" size={46} dur={8} className="absolute left-[8%] top-[16%] hidden md:block" interactive={false} />
      <BrandMark brand="samsung" size={64} dur={10} delay={1} className="absolute right-[10%] top-[12%] hidden md:block" interactive={false} />
      <BrandMark brand="google" size={44} dur={9} delay={0.5} className="absolute left-[14%] bottom-[14%] hidden md:block" interactive={false} />
      <DeviceDrift device="saturn" className="left-[28%] top-[5%] lg:left-[30%] lg:top-[6%]" imgClassName="h-[56px] lg:h-[110px]" dur={15} opacity="opacity-55 lg:opacity-75" />
      <DeviceDrift device="pluto" className="right-[28%] bottom-[6%] lg:right-[30%]" imgClassName="h-[38px] lg:h-[70px]" dur={13} delay={1} opacity="opacity-50 lg:opacity-70" />
      <PhraseFloat text={t("buy.perks")[0].title} tone="volt" className="left-[4%] top-[58%] xl:top-[46%]" dur={10} />
      <PhraseFloat text={t("buy.perks")[1].title} tone="cyan" className="right-[4%] top-[72%] xl:top-[62%]" dur={12} delay={1.4} />

      {/* vertical glowing rail of the phones we sell */}
      <TechRail
        testid="phones-tech-rail"
        className="absolute right-1 top-[54%] h-[30vh] w-36 -translate-y-1/2 opacity-70 2xl:right-2 2xl:h-[42vh] 2xl:w-64 2xl:opacity-100"
        speed={24}
        items={[
          { label: "iPhone 17 Pro Max", img: "/phones/iphone16.webp" },
          { label: "Galaxy S26 Ultra", img: "/phones/samsung.png" },
          { label: "Pixel 11 Pro", img: "/phones/pixel.png" },
          { label: "Galaxy A55", img: "/phones/a55.webp" },
          { label: "iPhone 16", img: "/phones/iphone16.webp" },
          { label: "Galaxy S20 +", img: "/phones/samsung.png" },
          { label: "Pixel 6 +", img: "/phones/pixel.png" },
        ]}
      />

      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-10 px-4 sm:px-8 lg:grid-cols-12 lg:px-16">
        <div className="lg:col-span-7">
          <motion.span
            data-testid="phones-badge"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: EASE }}
            className="inline-block rounded-full bg-volt px-5 py-2 font-mono text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-[#0B111B]"
          >
            {t("buy.eyebrow")}
          </motion.span>
          <h1 className="mt-7 font-display text-[15vw] sm:text-7xl lg:text-8xl uppercase leading-[0.98] tracking-tight">
            <MaskLines
              lines={[
                t("buy.titleA"),
                <span key="e" className="text-accent-bright">{t("buy.titleEm")}</span>,
                t("buy.titleB"),
              ]}
            />
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5, ease: EASE }}
            className="mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-ink/65"
          >
            {t("buy.lede")}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.65, ease: EASE }}
            className="mt-8 flex flex-wrap gap-3"
          >
            <a
              href={SITE.phoneHref}
              data-testid="phones-call-button"
              className="group inline-flex items-center gap-2.5 rounded-full bg-volt px-7 py-3.5 text-sm sm:text-base font-bold uppercase tracking-wide text-[#0B111B] transition-colors duration-300 hover:bg-accent hover:text-[#EDF1F7]"
            >
              <Phone className="h-4 w-4 transition-transform duration-300 group-hover:rotate-12" />
              {t("nav.call")} {SITE.phoneDisplay}
            </a>
          </motion.div>
        </div>

        <div className="relative hidden h-[26rem] lg:col-span-5 lg:block">
          <motion.div
            className="absolute right-0 top-0"
            animate={{ y: [0, -16, 0], rotate: [2, -2, 2] }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          >
            <PhoneFloat variant="iphone" imgClassName="h-[240px]" caption={false} />
          </motion.div>
          <motion.div
            className="absolute left-0 top-24"
            animate={{ y: [0, -20, 0], rotate: [-3, 3, -3] }}
            transition={{ duration: 9.5, delay: 0.7, repeat: Infinity, ease: "easeInOut" }}
          >
            <PhoneFloat variant="samsung" imgClassName="h-[150px] w-auto" caption={false} />
          </motion.div>
          <motion.div
            className="absolute bottom-0 right-16"
            animate={{ y: [0, -14, 0] }}
            transition={{ duration: 7.5, delay: 1.2, repeat: Infinity, ease: "easeInOut" }}
          >
            <motion.div animate={{ rotate: [0, 360] }} transition={{ duration: 50, repeat: Infinity, ease: "linear" }}>
              <PhoneFloat variant="pixel" imgClassName="h-[180px]" caption={false} />
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const BrandBoard = ({ brand, index }) => {
  const { t } = useLang();
  return (
    <Reveal delay={index * 0.08} className="h-full">
      <Tilt3D className="h-full">
      <div
        id={`brand-${brand.brandKey}`}
        data-testid={`phones-brand-${brand.id}`}
        className="glow-frame relative h-full overflow-visible rounded-[24px] border border-ink/10 bg-white/[0.04] p-6 sm:p-7"
      >
        <span aria-hidden="true" className="gf-corner left-2 top-2 border-l-2 border-t-2 border-volt/60" />
        <span aria-hidden="true" className="gf-corner right-2 top-2 border-r-2 border-t-2 border-volt/60" />
        <span aria-hidden="true" className="gf-corner bottom-2 left-2 border-b-2 border-l-2 border-volt/60" />
        <span aria-hidden="true" className="gf-corner bottom-2 right-2 border-b-2 border-r-2 border-volt/60" />
        <BrandMark
          brand={brand.brandKey}
          size={brand.brandKey === "samsung" ? 56 : 44}
          dur={6 + index}
          className="absolute -top-5 end-6 z-10"
        />
        <div className="flex items-end gap-4">
          <div className="flex items-end gap-2">
            {brand.images.map((src, i) => (
              <motion.img
                key={src}
                src={src}
                alt={`${brand.brand} phones for sale at Next-G`}
                className={`w-auto object-contain ${i === 0 ? "h-20" : "h-16 opacity-80"}`}
                style={{ filter: `drop-shadow(0 0 22px ${brand.glow})` }}
                animate={{ y: [0, -8 - i * 3, 0] }}
                transition={{ duration: 5 + i + index, repeat: Infinity, ease: "easeInOut" }}
              />
            ))}
          </div>
          <p className="font-display text-2xl uppercase tracking-tight">{brand.brand}</p>
        </div>
        {brand.groups.map((g) => (
          <div key={g.name || g.nameKey} className="mt-5">
            <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-ink/45">
              {g.name || t(g.nameKey)}
            </p>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {g.models.map((m) => (
                <span
                  key={m}
                  className="rounded-full border border-ink/12 bg-white/5 px-2.5 py-1 font-mono text-[10px] tracking-wide text-ink/70 transition-colors hover:border-volt hover:text-volt"
                >
                  {m}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
      </Tilt3D>
    </Reveal>
  );
};

export default function Phones() {
  const { t } = useLang();
  const perks = t("buy.perks");
  return (
    <>
      <Hero />
      <Marquee items={perks.map((p) => p.title)} />

      {/* scroll-through solar system of the lineup */}
      <section className="relative overflow-hidden border-b border-sand/60" aria-label={t("buy.brandsTitle")}>
        <SolarSystem
          testid="phones-solar-system"
          center={{ src: "/phones/iphone.png", label: t("buy.brandsTitle"), glow: "rgba(46,107,230,0.5)" }}
          bodies={[
            { src: "/phones/iphone16.webp", label: "iPhone 16", ring: 0, phase: 0, dur: 26, size: 64, glow: "rgba(46,107,230,0.45)" },
            { src: "/phones/a55.webp", label: "Galaxy A55", ring: 0, phase: 120, dur: 26, size: 60, glow: "rgba(79,179,191,0.45)" },
            { src: "/phones/ip15.png", label: "iPhone 15", ring: 0, phase: 240, dur: 26, size: 58, glow: "rgba(255,150,200,0.45)" },
            { src: "/phones/samsung.png", label: "Galaxy S26 Ultra", ring: 1, phase: 40, dur: 38, size: 84, glow: "rgba(61,111,224,0.5)" },
            { src: "/phones/ip17p.png", label: "iPhone 17 Pro", ring: 1, phase: 160, dur: 38, size: 82, glow: "rgba(130,150,255,0.5)" },
            { src: "/phones/zflip6.png", label: "Galaxy Z Flip 6", ring: 1, phase: 250, dur: 38, size: 72, glow: "rgba(255,220,90,0.5)" },
            { src: "/planets/moon.png", ring: 1, phase: 320, dur: 38, size: 38, glow: "rgba(244,244,239,0.4)", decorative: true },
            { src: "/phones/iphone.png", label: "iPhone 17 Pro Max", ring: 2, phase: 300, dur: 52, size: 78, glow: "rgba(124,131,219,0.4)" },
            { src: "/phones/ip16pm.png", label: "iPhone 16 Pro Max", ring: 2, phase: 80, dur: 52, size: 74, glow: "rgba(130,150,255,0.45)" },
            { src: "/phones/pixel.png", label: "Pixel 9 Pro", ring: 2, phase: 200, dur: 52, size: 70, glow: "rgba(127,224,195,0.45)" },
            { src: "/phones/ip15pm.png", label: "iPhone 15 Pro Max", ring: 2, phase: 140, dur: 52, size: 70, glow: "rgba(226,196,164,0.45)" },
            { src: "/planets/pluto.png", ring: 2, phase: 350, dur: 52, size: 34, glow: "rgba(220,200,180,0.45)", decorative: true },
          ]}
        />
      </section>

      <section className="relative mx-auto max-w-7xl px-4 py-16 sm:px-8 sm:py-24 lg:px-16">
        <BrandMark brand="google" size={30} dur={11} className="absolute right-[6%] top-6 opacity-40" interactive={false} />
        <Stagger className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {perks.map((p, i) => {
            const Icon = PERK_ICONS[i % PERK_ICONS.length];
            return (
              <StaggerItem key={p.title}>
                <Jelly className="h-full">
                  <div className="glass-liquid group h-full rounded-2xl p-6 transition-transform duration-500 hover:-translate-y-1.5">
                    <span className={`inline-flex h-11 w-11 items-center justify-center rounded-full border border-ink/15 ${PERK_COLORS[i % 4]}`}>
                      <Icon className="h-5 w-5" />
                    </span>
                    <p className="mt-4 font-display text-lg uppercase tracking-tight">{p.title}</p>
                    <p className="mt-2 text-sm leading-relaxed text-ink/60">{p.body}</p>
                  </div>
                </Jelly>
              </StaggerItem>
            );
          })}
        </Stagger>
      </section>

      <section className="relative mx-auto max-w-7xl px-4 pb-20 sm:px-8 lg:px-16">
        <PhoneBgDrift variant="iphone" className="-left-12 top-24 h-[300px] hidden lg:block" />
        <Reveal>
          <p className="eyebrow">{t("buy.brandsTitle")}</p>
        </Reveal>
        <div className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-3">
          {PHONE_SERIES.map((b, i) => (
            <BrandBoard key={b.id} brand={b} index={i} />
          ))}
        </div>
        <Reveal className="mt-6">
          <p className="text-sm text-ink/55">{t("buy.seriesNote")}</p>
        </Reveal>
      </section>

      <BrandMarquee testid="phones-brand-marquee" />

      <section className="mx-auto max-w-7xl px-4 pb-24 sm:px-8 lg:px-16">
        <Reveal>
          <div className="flex flex-col items-start gap-6 rounded-[24px] bg-volt px-6 py-10 sm:flex-row sm:items-center sm:justify-between sm:px-12">
            <p className="font-display text-2xl sm:text-4xl uppercase leading-tight tracking-tight text-[#0B111B]">
              {t("buy.bestPrice")}
            </p>
            <a
              href={SITE.phoneHref}
              data-testid="phones-stock-call-button"
              className="group inline-flex shrink-0 items-center gap-2.5 rounded-full bg-[#0B111B] px-8 py-4 text-sm sm:text-base font-bold uppercase tracking-wide text-volt transition-colors duration-300 hover:bg-accent hover:text-[#EDF1F7]"
            >
              {t("buy.cta")}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 rtl:-scale-x-100" />
            </a>
          </div>
        </Reveal>
      </section>
    </>
  );
}
